import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { authApi, errorMessage, unwrap } from "@/api/client";
import { Alert, Field, PasswordField } from "@/ui";

const schema = z
  .object({
    display_name: z.string().min(2, "Минимум 2 символа").max(40, "Максимум 40 символов"),
    email: z.string().email("Введите email"),
    password: z.string().min(8, "Минимум 8 символов"),
    confirm: z.string(),
  })
  .refine((f) => f.password === f.confirm, { path: ["confirm"], message: "Пароли не совпадают" });
type Form = z.infer<typeof schema>;

export function RegisterPage() {
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<Form>({ resolver: zodResolver(schema) });

  const onSubmit = async (f: Form) => {
    setError(null);
    try {
      unwrap(
        await authApi.POST("/api/v1/auth/register", {
          body: { email: f.email, password: f.password, display_name: f.display_name },
        }),
      );
      nav("/verify-email", { state: { email: f.email } });
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <form className="auth-card" onSubmit={handleSubmit(onSubmit)} noValidate>
      <div>
        <h1>Создать аккаунт</h1>
        <p className="auth-card__sub">
          Уже есть аккаунт? <Link to="/login">Войти</Link>
        </p>
      </div>
      {error && <Alert kind="error">{error}</Alert>}
      <Field
        label="Имя"
        placeholder="Как вас называть"
        autoComplete="nickname"
        error={formState.errors.display_name?.message}
        {...register("display_name")}
      />
      <Field
        label="Email"
        type="email"
        placeholder="you@example.com"
        autoComplete="email"
        error={formState.errors.email?.message}
        {...register("email")}
      />
      <PasswordField
        label="Пароль"
        id="password"
        autoComplete="new-password"
        hint="Минимум 8 символов"
        error={formState.errors.password?.message}
        {...register("password")}
      />
      <PasswordField
        label="Повторите пароль"
        id="confirm"
        autoComplete="new-password"
        error={formState.errors.confirm?.message}
        {...register("confirm")}
      />
      <button className="btn btn--block" type="submit" disabled={formState.isSubmitting}>
        Создать аккаунт
      </button>
      <p className="small muted" style={{ margin: 0 }}>
        На почту придёт письмо со ссылкой для подтверждения. Письма мы шлём только по операциям с
        аккаунтом.
      </p>
    </form>
  );
}
